import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const PopupOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
`;

const PopupContainer = styled(motion.div)`
  background-color: var(--card-background);
  background-image: linear-gradient(to bottom, rgba(92, 67, 169, 0.2), rgba(26, 13, 58, 0.2));
  border-radius: var(--border-radius);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5);
  width: 100%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 1.5rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, var(--primary-color), var(--accent-color));
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--text-color);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 2;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ImageContainer = styled.div`
  width: 64px;
  height: 64px;
  background-image: ${props => `url(${props.$image})`};
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
  margin-right: 1rem;
  flex-shrink: 0;
  background-color: var(--primary-color);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
`;

const HeaderInfo = styled.div`
  flex-grow: 1;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
  color: var(--text-color);
`;

const Subtitle = styled.h4`
  font-size: 1rem;
  color: var(--accent-color);
  font-weight: 400;
`;

const BadgeContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  background-color: ${props => props.$color || 'var(--primary-color)'};
  color: var(--text-color);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  
  &::after {
    content: '';
    flex-grow: 1;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
    margin-left: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const StatItem = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0.5rem;
  border-radius: 4px;
`;

const StatName = styled.div`
  font-size: 0.75rem;
  color: var(--accent-color);
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  font-size: 1rem;
  font-weight: 600;
`;

const Description = styled.div`
  font-size: 0.95rem;
  line-height: 1.6;
  margin-top: 1rem;
  white-space: pre-line;
`;

const StanceTabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const StanceTab = styled.button`
  background-color: ${props => props.$active ? 'var(--accent-color)' : 'rgba(46, 23, 96, 0.6)'};
  color: ${props => props.$active ? 'var(--background-color)' : 'var(--text-color)'};
  border-radius: 4px;
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$active ? 'var(--accent-color)' : 'rgba(92, 67, 169, 0.4)'};
  }
`;

// Function to determine color based on type
const getTypeColor = (type) => {
  if (!type) return 'var(--primary-color)';
  
  switch(type.toLowerCase()) {
    case 'starter':
      return '#4caf50'; // Green
    case 'relic':
      return '#ff9800'; // Orange
    case 'physical':
      return '#f44336'; // Red
    case 'magical':
      return '#2196f3'; // Blue
    case 'basic':
      return '#9e9e9e'; // Grey
    case 'passive':
      return '#ffeb3b'; // Yellow
    case 'ability 1':
    case 'ability 2':
    case 'ability 3':
      return '#2196f3'; // Blue
    case 'ultimate':
      return '#f44336'; // Red
    default:
      return 'var(--primary-color)'; // Default
  }
};

// Function to clean interactions for stance-switching abilities
const cleanInteractionEffects = (data, activeStance) => {
  if (!data || !data.interaction_effects) return [];
  
  // Filter interactions based on stance mentions
  // Some interaction effects may contain stance information and should only be shown for that stance
  return data.interaction_effects.filter(effect => {
    const effectText = effect.effect || '';
    
    // Always include effects that don't mention stances
    if (!effectText.includes('Bow:') && !effectText.includes('Axe:')) {
      return true;
    }
    
    // For stance-specific effects, only include those that match the current stance
    return effectText.includes(`${activeStance}:`);
  });
};

const InfoPopup = ({ data, isOpen, onClose, type }) => {
  const [activeStance, setActiveStance] = useState('');
  
  // Detect if an ability description contains multiple stance descriptions
  const detectStanceDescription = (ability) => {
    if (!ability) return false;
    
    // Already tagged as stance switcher
    if (ability.is_stance_switcher && ability.stance_versions && ability.stance_versions.length > 0) {
      return true;
    }
    
    // Look for patterns like "Bow: ... Axe: ..." in the description
    const description = ability.description || '';
    const stanceKeywords = ['Bow:', 'Axe:', 'Bow Stance:', 'Axe Stance:'];
    
    // Count how many stance indicators we have
    let stanceCount = 0;
    for (const keyword of stanceKeywords) {
      if (description.includes(keyword)) {
        stanceCount++;
      }
    }
    
    // If multiple stance indicators, this is a stance description
    return stanceCount > 1;
  };
  
  // Parse an ability description to extract stance versions
  const parseStanceVersions = (ability) => {
    if (!ability) return [];
    
    if (ability.stance_versions && ability.stance_versions.length > 0) {
      return ability.stance_versions;
    }
    
    const description = ability.description || '';
    const stances = [];
    
    // Special case for abilities like Expose Weakness where stances are in one line
    // Example: "Bow: Gain bonus Attack Speed. Axe Gain bonus Movement Speed."
    if (description.includes('Bow:') && description.includes('Axe') && 
        !description.includes('Axe:') && description.includes('.')) {
      
      // Try to split by period and look for Bow/Axe segments
      const segments = description.split('.');
      
      let bowDescription = '';
      let axeDescription = '';
      
      for (const segment of segments) {
        const trimmed = segment.trim();
        if (trimmed.startsWith('Bow:')) {
          bowDescription = trimmed.substring(4).trim();
        } else if (trimmed.startsWith('Bow')) {
          bowDescription = trimmed.substring(3).trim();
        } else if (trimmed.startsWith('Axe:')) {
          axeDescription = trimmed.substring(4).trim();
        } else if (trimmed.startsWith('Axe')) {
          axeDescription = trimmed.substring(3).trim();
        }
      }
      
      if (bowDescription) {
        stances.push({
          stance: 'Bow',
          description: bowDescription
        });
      }
      
      if (axeDescription) {
        stances.push({
          stance: 'Axe',
          description: axeDescription
        });
      }
      
      if (stances.length > 0) {
        return stances;
      }
    }
    
    // Special case for repeated stance descriptions (like in Hail of Arrows)
    // If we see multiple instances of the same stance label, we need to handle it specially
    const stanceKeywords = ['Bow:', 'Axe:'];
    const repeatedStances = new Map();
    
    stanceKeywords.forEach(keyword => {
      let count = 0;
      let pos = description.indexOf(keyword);
      while (pos !== -1) {
        count++;
        pos = description.indexOf(keyword, pos + 1);
      }
      if (count > 1) {
        repeatedStances.set(keyword, count);
      }
    });
    
    // If we have repeated stances, we need special handling
    if (repeatedStances.size > 0) {
      // eslint-disable-next-line no-unused-vars
      for (const [keyword, _count] of repeatedStances.entries()) {
        // Find all instances of this stance
        const stanceDescriptions = [];
        const stanceName = keyword.replace(':', '');
        
        // Use regex to find all occurrences of this stance's descriptions
        const stanceRegex = new RegExp(`${keyword}\\s+([^\\n]+(?:\\n(?!${stanceKeywords.join('|')})[^\\n]+)*)`, 'g');
        let match;
        while ((match = stanceRegex.exec(description)) !== null) {
          if (match[1]) {
            stanceDescriptions.push(match[1].trim());
          }
        }
        
        // If we found multiple descriptions, use the longest one as it likely has the most detail
        if (stanceDescriptions.length > 1) {
          const longestDescription = stanceDescriptions.reduce(
            (longest, current) => current.length > longest.length ? current : longest, 
            stanceDescriptions[0]
          );
          
          stances.push({
            stance: stanceName,
            description: longestDescription
          });
        } 
        // Otherwise just use the single one we found
        else if (stanceDescriptions.length === 1) {
          stances.push({
            stance: stanceName,
            description: stanceDescriptions[0]
          });
        }
      }
      
      // Also add any non-repeated stances
      stanceKeywords.forEach(keyword => {
        if (!repeatedStances.has(keyword)) {
          const stanceName = keyword.replace(':', '');
          const stanceRegex = new RegExp(`${keyword}\\s+([^\\n]+(?:\\n(?!${stanceKeywords.join('|')})[^\\n]+)*)`, 'g');
          let match = stanceRegex.exec(description);
          if (match && match[1]) {
            stances.push({
              stance: stanceName,
              description: match[1].trim()
            });
          }
        }
      });
      
      // If we successfully processed repeated stances, return the result
      if (stances.length > 0) {
        return stances;
      }
    }
    
    // If the above special case handling didn't work, fall back to regular stance extraction
    
    // First, find all stance keywords in the text
    const stancePositions = [];
    
    stanceKeywords.forEach(keyword => {
      let pos = description.indexOf(keyword);
      while (pos !== -1) {
        stancePositions.push({ position: pos, keyword });
        pos = description.indexOf(keyword, pos + 1);
      }
    });
    
    // Sort by position in text
    stancePositions.sort((a, b) => a.position - b.position);
    
    // Extract each stance's description
    stancePositions.forEach((stance, index) => {
      const startPos = stance.position + stance.keyword.length;
      const endPos = index < stancePositions.length - 1 
        ? stancePositions[index + 1].position 
        : description.length;
        
      const stanceText = description.substring(startPos, endPos).trim();
      
      // Add to stances array, but check if we already have this stance
      const existingStance = stances.find(s => s.stance === stance.keyword.replace(':', ''));
      if (existingStance) {
        // If we already have this stance, use the longer description
        if (stanceText.length > existingStance.description.length) {
          existingStance.description = stanceText;
        }
      } else {
        stances.push({
          stance: stance.keyword.replace(':', ''),
          description: stanceText
        });
      }
    });
    
    // If we found proper stance descriptions, return them
    if (stances.length > 0) {
      return stances;
    }
    
    // Fallback to the original regex approach
    const bowMatch = description.match(/Bow:?\s+(.+?)(?=Axe:|$)/s);
    const axeMatch = description.match(/Axe:?\s+(.+?)(?=Bow:|$)/s);
    
    if (bowMatch && bowMatch[1]) {
      stances.push({
        stance: 'Bow',
        description: bowMatch[1].trim()
      });
    }
    
    if (axeMatch && axeMatch[1]) {
      stances.push({
        stance: 'Axe',
        description: axeMatch[1].trim()
      });
    }
    
    return stances;
  };
  
  useEffect(() => {
    // Reset active stance when data changes
    if (data) {
      const stanceVersions = data.stance_versions && data.stance_versions.length > 0 
        ? data.stance_versions 
        : parseStanceVersions(data);
        
      if (stanceVersions && stanceVersions.length > 0) {
        setActiveStance(stanceVersions[0].stance);
      }
    }
  }, [data]);
  
  if (!data) return null;
  
  const isItem = type === 'item';
  const isAbility = type === 'ability';
  
  // Determine if this is a stance-switching ability
  const isStanceSwitcher = isAbility && (
    data.is_stance_switcher || detectStanceDescription(data)
  );
  
  // Get stance versions either from data or by parsing the description
  const stanceVersions = isStanceSwitcher ? (
    data.stance_versions && data.stance_versions.length > 0 
      ? data.stance_versions 
      : parseStanceVersions(data)
  ) : [];
  
  // Get active stance data
  const getActiveStanceData = () => {
    if (!isStanceSwitcher || stanceVersions.length === 0) return null;
    return stanceVersions.find(sv => sv.stance === activeStance) || stanceVersions[0];
  };
  
  const handleStanceChange = (stance) => {
    setActiveStance(stance);
  };
  
  const activeStanceData = getActiveStanceData();
  
  // Get cleaned interactions for the current stance
  const getStanceInteractions = () => {
    if (!isAbility || !data.has_interactions || !data.interaction_effects) {
      return [];
    }
    
    if (isStanceSwitcher && activeStanceData) {
      return cleanInteractionEffects(data, activeStance);
    }
    
    return data.interaction_effects;
  };
  
  const interactionEffects = getStanceInteractions();

  // Fix for Hail of Arrows type abilities - clean up duplicate descriptions
  const getCleanDescription = () => {
    if (!data) return '';
    
    // For stance abilities in the popup, use only the stance description
    if (isStanceSwitcher && activeStanceData) {
      return activeStanceData.description;
    }
    
    // Special handling for abilities with duplicate descriptions
    if (isAbility && data.description && data.description.includes('\n\n')) {
      const paragraphs = data.description.split('\n\n');
      if (paragraphs.length > 1) {
        // Find and remove duplicate paragraphs
        const uniqueParagraphs = [];
        for (const paragraph of paragraphs) {
          // Only add this paragraph if it's not already included in another one
          const isDuplicate = uniqueParagraphs.some(p => 
            p.includes(paragraph) || paragraph.includes(p)
          );
          
          if (!isDuplicate) {
            uniqueParagraphs.push(paragraph);
          } else {
            // If it's a duplicate but longer, replace the shorter one
            const shorterIdx = uniqueParagraphs.findIndex(p => 
              p.includes(paragraph) || paragraph.includes(p)
            );
            if (shorterIdx !== -1 && paragraph.length > uniqueParagraphs[shorterIdx].length) {
              uniqueParagraphs[shorterIdx] = paragraph;
            }
          }
        }
        
        return uniqueParagraphs.join('\n\n');
      }
    }
    
    // For non-stance abilities, just use the regular description
    return data.description;
  };
  
  const cleanDescription = getCleanDescription();
  
  return (
    <AnimatePresence>
      {isOpen && (
        <PopupOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <PopupContainer
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={onClose}>×</CloseButton>
            
            <Header>
              <ImageContainer $image={isItem ? data.icon_url : data.image_url} />
              <HeaderInfo>
                <Title>{data.name}</Title>
                {isItem && <Subtitle>{data.type}</Subtitle>}
                {isAbility && <Subtitle>{data.ability_type}</Subtitle>}
                
                <BadgeContainer>
                  {isItem && data.type && (
                    <Badge $color={getTypeColor(data.type)}>
                      {data.type}
                    </Badge>
                  )}
                  {isItem && data.total_gold_cost > 0 && (
                    <Badge $color="#ffc107">
                      Cost: {data.total_gold_cost}
                    </Badge>
                  )}
                  {isAbility && data.ability_type && (
                    <Badge $color={getTypeColor(data.ability_type)}>
                      {data.ability_type}
                    </Badge>
                  )}
                </BadgeContainer>
              </HeaderInfo>
            </Header>
            
            {isStanceSwitcher && stanceVersions.length > 0 && (
              <StanceTabsContainer>
                {stanceVersions.map((sv) => (
                  <StanceTab 
                    key={sv.stance}
                    $active={activeStance === sv.stance}
                    onClick={() => handleStanceChange(sv.stance)}
                  >
                    {sv.stance}
                  </StanceTab>
                ))}
              </StanceTabsContainer>
            )}
            
            {isItem && data.stats && data.stats.length > 0 && (
              <Section>
                <SectionTitle>Stats</SectionTitle>
                <StatsGrid>
                  {data.stats.map((stat, index) => (
                    <StatItem key={index}>
                      <StatName>{stat.DisplayName.replace(/:\s*$/, '')}</StatName>
                      <StatValue>{String(stat.Value).replace(/^\s*:/, '')}</StatValue>
                    </StatItem>
                  ))}
                </StatsGrid>
              </Section>
            )}
            
            {isAbility && data.stats && Object.keys(data.stats).length > 0 && (
              <Section>
                <SectionTitle>Stats</SectionTitle>
                <StatsGrid>
                  {Object.entries(data.stats).map(([key, value], index) => (
                    <StatItem key={index}>
                      <StatName>{key.replace(/:\s*$/, '')}</StatName>
                      <StatValue>{String(value).replace(/^\s*:/, '')}</StatValue>
                    </StatItem>
                  ))}
                </StatsGrid>
              </Section>
            )}
            
            <Section>
              <SectionTitle>Description</SectionTitle>
              {isStanceSwitcher && activeStanceData ? (
                <>
                  {/* Don't show base effect when a specific stance is selected */}
                  <Description>
                    <strong>{activeStance}:</strong> {activeStanceData.description}
                  </Description>
                </>
              ) : (
                <Description>{cleanDescription}</Description>
              )}
            </Section>
            
            {interactionEffects && interactionEffects.length > 0 && (
              <Section>
                <SectionTitle>Interactions</SectionTitle>
                {interactionEffects.map((effect, index) => (
                  <Description key={index}>
                    {effect.effect}
                  </Description>
                ))}
              </Section>
            )}
            
            {isAbility && data.has_aspect && data.aspect_description && (
              <Section>
                <SectionTitle>Aspect</SectionTitle>
                <Description>{data.aspect_description}</Description>
              </Section>
            )}
            
            {isAbility && data.notes && data.notes.length > 0 && (
              <Section>
                <SectionTitle>Notes</SectionTitle>
                {data.notes.map((note, index) => (
                  <Description key={index}>{note}</Description>
                ))}
              </Section>
            )}
          </PopupContainer>
        </PopupOverlay>
      )}
    </AnimatePresence>
  );
};

export default InfoPopup; 